import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import snakeCaseKeys from 'snakecase-keys';
import { useAbility } from '@casl/react';
import { markingCorrectionAttendanceReport } from '../../../requests/legalreports';
import { sendAlert } from '../../../actions/utils';
import { ComponentDataTable, SimpleCenteredModal, DefaultModal } from '../../../components';
import AttendanceReplaceForm from './AttendanceReplaceForm';
import { AbilityContext } from '../../../config/abilityContext';
import Filters from './Filters';
import AttendanceShow from './AttendanceShow';
import MassiveActions from './MassiveActios/MassiveActions';
import useMassiveActionHook from './MassiveActios/useMassiveActionHook';
import { createAttendanceChangeRequest } from '../../../requests/AttendanceChange';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import AttendanceUpdateShiftForm from './AttendanceUpdateShiftForm';
import { debounceIndexShiftsRequest } from '../../../requests/shifts';
import {
  createAttendanceChageRequest,
  changeStatusAttendanceRequest,
  updateAttendanceRequest,
  updateAttendanceShiftRequest
} from '../../../requests/attendances';
import AttendanceUpdateForm from './AttendanceUpdateForm';
import './Style.scss';

export const LoadingTranslation = () => {
  return <h3>Cargando...</h3>;
};

const MarkingCorrectionDatatable = ({ moreData, setMoreData, columns, branchOffices }) => {
  const ability = useAbility(AbilityContext);
  const [onRequest, setOnRequest] = useState(false);
  const [attendances, setAttendances] = useState([]);
  const [simpleModalShow, setSimpleModalShow] = useState(false);
  const [simpleModalBody, setSimpleModalBody] = useState({});
  const [simpleModalTitle, setSimpleModalTitle] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState('');
  const [size, setSize] = useState('lg');
  const [modalAction, setModalAction] = useState(() => null);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [amount, setAmount] = useState(0);
  const [centerModalShow, setCenterModalShow] = useState(false);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const [filters, setFilters] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const dispatch = useDispatch();
  const { currentCompany } = useSelector(state => state.auth);
  const [shifts, setShifts] = useState([]);

  const fetchContractShift = () => {
    debounceIndexShiftsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => {
        const { data } = response.data;
        setShifts(data);
      }
    });
  };

  useEffect(fetchContractShift, []);

  const handleModalClose = () => {
    setCenterModalShow(false);
    setModalShow(false);
    setSimpleModalShow(false);
    setClearSelectedRows(!clearSelectedRows);
  };

  const handleSuccessRequest = status => {
    let message = '';
    setOnRequest(true);
    setMoreData(!moreData);
    switch (status) {
      case 'updated':
        message = 'Solicitud de Correción de Marcación realizada con exito.';
        break;
      case 'disabled':
        message = 'Registro de asistencia desactivado exitosamente.';
        break;
      case 'active':
        message = 'Registro de asistencia activado exitosamente.';
        break;
      case 'massive-updated':
        message = 'Solicitud masiva de Correción de Marcación realizada con exito.';
        break;
      case 'massive-approved':
        message = 'Activación masiva de correciones de marcación realizada conexito.';
        break;
      case 'massive-disabled':
        message = 'Desactivación masiva de marcaciones realizada con éxito.';
        break;
      case 'massive-active':
        message = 'Activación masiva de marcaciones realizada con éxito';
        break;
      case 'massive-create-attendance':
        message = 'Solicitud de creación masiva de asistencia realizada con exito.';
        break;
      default:
        message = 'No action';
        break;
    }

    dispatch(sendAlert({ kind: 'success', message }));
    handleModalClose();
  };

  const handleFailureRequest = (error, acction = null) => {
    const { response } = error;
    if (acction === 'massive-updated' && Array.isArray(response?.data?.message)) {
      const groupedErrors = response?.data?.message.map(item => ({
        employee: item.employee,
        errors: [...new Set(item.errors.flat())]
      }));

      const body = groupedErrors.map(errors => (
        <div>
          <p>
            <strong>Trabajador:</strong> {errors.employee}
          </p>
          <p>
            <strong>Errores:</strong> {errors.errors.join(', ')}{' '}
          </p>
          <hr />
        </div>
      ));
      setSimpleModalTitle('Se encontraron los siguiente errores');
      setSimpleModalBody(<>{body}</>);
      setSimpleModalShow(true);
      setSize('lg');
      setClearSelectedRows(!clearSelectedRows);
    } else {
      dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
      setClearSelectedRows(!clearSelectedRows);
    }
  };

  const handleRequest = (params, format, responseType = 'json') => {
    if (Object.keys(params).length > 0) {
      setOnRequest(true);
      const empParams = { ...params };
      delete empParams.date_to;
      delete empParams.date_from;
      markingCorrectionAttendanceReport({
        dispatch,
        params: { ...filters, ...empParams },
        format,
        responseType,
        successCallback: response => {
          const { data } = response;
          setAmount(data.metadata.amount);
          setAttendances(data.data);
          setOnRequest(false);
        },
        callback: () => setOnRequest(false)
      });
    }
  };
  const handleFilters = params => {
    setFilters(params);
    setSearchFirstStart(true);
    setClearSelectedRows(!clearSelectedRows);
    handleRequest(params);
  };

  const handleUpdateRequest = (attendance, setSubmitting) => {
    createAttendanceChangeRequest({
      dispatch,
      params: {
        ...snakeCaseKeys(attendance),
        front_company_id: currentCompany?.id
      },
      successCallback: () => {
        handleSuccessRequest('updated');
        setSubmitting(false);
        setOnRequest(false);
      },
      failureCallback: error => {
        handleFailureRequest(error);
        setSubmitting(false);
        setOnRequest(false);
      }
    });
  };

  const handleUpdateShiftRequest = (attendance, setSubmitting) => {
    updateAttendanceShiftRequest({
      dispatch,
      params: {
        ...snakeCaseKeys(attendance)
      },
      successCallback: () => {
        handleSuccessRequest('updated');
        setSubmitting(false);
        setOnRequest(false);
      },
      failureCallback: error => {
        handleFailureRequest(error);
        setSubmitting(false);
        setOnRequest(false);
      }
    });
  };

  const handleUpdateAttendanceRequest = (params, setSubmitting) => {
    const { id, ...attendanceWithoutId } = params.attendance;
    if (id) {
      updateAttendanceRequest(id, {
        dispatch,
        params: {
          attendance: {
            ...snakeCaseKeys(attendanceWithoutId)
          }
        },
        successCallback: () => {
          handleSuccessRequest('updated');
          setSubmitting(false);
          setOnRequest(false);
        },
        failureCallback: error => {
          handleFailureRequest(error);
          setSubmitting(false);
          setOnRequest(false);
        }
      });
    }
    if (!id) {
      createAttendanceChageRequest({
        dispatch,
        params: {
          attendance: {
            ...snakeCaseKeys(attendanceWithoutId)
          }
        },
        successCallback: () => {
          handleSuccessRequest('updated');
          setSubmitting(false);
          setOnRequest(false);
        },
        failureCallback: error => {
          handleFailureRequest(error);
          setSubmitting(false);
          setOnRequest(false);
        }
      });
    }
  };

  const handleUpdateModal = attendance => {
    const parsedAttendance = camelCaseRecursive(attendance);
    const correctionType = !parsedAttendance.arrivalMark && !parsedAttendance.departureMark ? 'add' : 'replacing';
    setSimpleModalBody(
      <AttendanceReplaceForm
        attendance={parsedAttendance}
        correctionType={correctionType}
        formRequest={handleUpdateRequest}
        handleModalClose={handleModalClose}
      />
    );

    setSimpleModalTitle('Correción de Marcación');
    setSimpleModalShow(true);
    setSize('lg');
  };

  const handleUpdateShiftModal = attendance => {
    const parsedAttendance = camelCaseRecursive(attendance);
    setSimpleModalBody(
      <AttendanceUpdateShiftForm
        attendance={parsedAttendance}
        formRequest={handleUpdateShiftRequest}
        handleModalClose={handleModalClose}
        shifts={shifts}
      />
    );
    setSimpleModalTitle('Correción de Marcación');
    setSimpleModalShow(true);
    setSize('md');
  };

  const handleUpdateAttendanceModal = (attendance, timeType) => {
    const parsedAttendance = camelCaseRecursive(attendance);
    setSimpleModalBody(
      <AttendanceUpdateForm
        attendance={parsedAttendance}
        formRequest={handleUpdateAttendanceRequest}
        handleModalClose={handleModalClose}
        shifts={shifts}
        timeType={timeType}
        branchOffices={branchOffices}
      />
    );
    setSimpleModalTitle('Correción de Marcación');
    setSimpleModalShow(true);
    setSize('lg');
  };

  const translateField = field => {
    const translations = {
      arrival: 'Entrada',
      arrival_break_time: 'Entrada Colación',
      departure_break_time: 'Salida Colación',
      departure: 'Salida'
    };

    return translations[field];
  };

  const handleDestroyAttendanceModal = (attendance, timeType) => {
    setModalBody(
      <>
        <p>Se cambiará de estado la siguiente marcación.</p>
        <ul>
          <li>
            <strong>Fecha:</strong> {attendance.date}
          </li>
          <li>
            <strong>Fecha y hora de la marcación:</strong> {attendance.parsedTime}
          </li>
          <li>
            <strong>Tipo de marcación:</strong> {translateField(timeType)}
          </li>
        </ul>
        <p>¿Estás seguro que deseas realizar esta acción?</p>
      </>
    );
    setModalTitle('Confirmación de cambio de estado');
    setModalShow(true);
    setSize('lg');
  };

  const handleDestroyAttendance = item => {
    changeStatusAttendanceRequest({
      dispatch,
      params: { attendance: { id: item.id } },
      successCallback: () => handleSuccessRequest('disabled'),
      failureCallback: error => handleFailureRequest(error)
    });
  };

  const handleShowModal = item => {
    const parsedAttendance = camelCaseEmptyStringRecursive(item);
    setSimpleModalTitle(`Asistencia - ${item.name}`);
    setSimpleModalShow(true);
    setSimpleModalBody(<AttendanceShow attendance={parsedAttendance} />);
    setSize('xl');
  };

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const handleActions = (item, action, timeType) => {
    switch (action) {
      case 'show':
        handleShowModal(item);
        break;
      case 'edit':
        handleUpdateModal(item);
        break;
      case 'edit_shift':
        handleUpdateShiftModal(item);
        break;
      case 'edit_attendance':
        handleUpdateAttendanceModal(item, timeType);
        break;
      case 'destroy_attendance':
        handleDestroyAttendanceModal(item, timeType);
        setModalItem(item);
        setModalAction(() => handleDestroyAttendance);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
        break;
    }
  };

  const employeeMenu = [
    ability.can('create', 'Attendance'),
    ability.can('read', 'Attendance'),
    ability.can('update', 'Attendance'),
    ability.can('destroy', 'Attendance')
  ];

  const { handleMassiveEdit, handleConfirmMassive } = useMassiveActionHook(
    selectedRows,
    dispatch,
    setSimpleModalBody,
    setSimpleModalTitle,
    setSimpleModalShow,
    setModalTitle,
    setModalBody,
    setCenterModalShow,
    setSize,
    handleSuccessRequest,
    handleFailureRequest,
    handleModalClose,
    branchOffices
  );

  const handleMassAction = action => {
    switch (action) {
      case 'massive-edit':
        handleMassiveEdit();
        break;
      case 'massive-approved':
        handleConfirmMassive('approved');
        break;
      case 'massive-disabled':
        handleConfirmMassive('disabled');
        break;
      case 'massive-active':
        handleConfirmMassive('active');
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  return (
    <>
      <Filters formRequest={handleFilters} isSubmit={onRequest} />
      <div className="data-table-mc">
        <ComponentDataTable
          onRequest={onRequest}
          responsive
          columns={columns(handleActions, employeeMenu)}
          data={attendances}
          totalRows={amount}
          resourceRequest={handleRequest}
          searchFirstStart={searchFirstStart}
          onRowClicked={null}
          progressPending={onRequest}
          progressComponent={<LoadingTranslation />}
          massActions={
            <MassiveActions handleClick={handleMassAction} disabled={!selectedCount} employeeMenu={employeeMenu} />
          }
          preName="attendance"
          selectableRows
          clearSelectedRows={clearSelectedRows}
          onSelectedRowsChange={handleSelectedRows}
          withMassActions
          moreData={moreData}
          withSearch={false}
        />
      </div>

      <SimpleCenteredModal
        title={simpleModalTitle}
        body={simpleModalBody}
        size={size}
        show={simpleModalShow}
        onHide={handleModalClose}
      />

      <SimpleCenteredModal
        size="lg"
        title={modalTitle}
        body={modalBody}
        show={centerModalShow}
        onHide={handleModalClose}
      />
      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction(modalItem)}
        disabled={onRequest}
        variantBtnClose="outline-info"
        variantBtnSave="primary"
      />
    </>
  );
};

export default MarkingCorrectionDatatable;
